import {
  endorsementWizard,
  type EndorsementAddress,
  type EndorsementChangeField,
  type EndorsementChangeScope,
  type EndorsementChangeValue,
  type EndorsementTypeCode,
  type PolicyParty,
} from '@/shared/api/resources';
import { formatCivilDate, formatMoney } from '@/shared/lib/format';

/**
 * Apoio do assistente de endosso (P-END-1): os passos, os enderecos das telas, os
 * nomes dos dados alterados e a escrita dos valores do antes e do depois.
 *
 * Os passos sao os do OnPoint, com a mesma chave da rota de cada um.
 */
export const passosDoEndosso = [
  'informacoes-do-endosso',
  'motivo-e-documentos',
  'resumo-da-alteracao',
  'informacoes-de-pagamento',
] as const;

export type PassoDoEndosso = (typeof passosDoEndosso)[number];

export const rotulosDosPassos: Record<PassoDoEndosso, string> = {
  'informacoes-do-endosso': 'Informacoes do endosso',
  'motivo-e-documentos': 'Motivo e documentos',
  'resumo-da-alteracao': 'Resumo da alteracao',
  'informacoes-de-pagamento': 'Informacoes de pagamento',
};

/** Titulo e trecho da trilha de cada assistente (END-012). */
export const tituloDoAssistente: Record<
  EndorsementTypeCode,
  { titulo: string; trilha: string }
> = {
  neutro: { titulo: 'Endosso neutro', trilha: 'Neutro' },
  alteracao: { titulo: 'Endosso de alteracao', trilha: 'Alteracao' },
  cancelamento: { titulo: 'Endosso de cancelamento', trilha: 'Cancelamento' },
  aniversario: { titulo: 'Endosso de aniversario', trilha: 'Aniversario' },
};

export const codigosDeEndosso: readonly EndorsementTypeCode[] = [
  'neutro',
  'alteracao',
  'cancelamento',
  'aniversario',
];

export function codigoDeEndosso(valor: string | undefined): EndorsementTypeCode | null {
  return codigosDeEndosso.find((codigo) => codigo === valor) ?? null;
}

/** Endereco da tela de selecao do tipo e subtipo. */
export function caminhoDaSelecao(policyId: string, tipo?: EndorsementTypeCode): string {
  return `/apolices/${policyId}/endosso${tipo ? `?tipo=${tipo}` : ''}`;
}

/** Endereco do assistente, com o rascunho quando ja existe. */
export function caminhoDoAssistente(
  policyId: string,
  tipo: EndorsementTypeCode,
  subtypeId: string,
  endorsementId?: string,
): string {
  const base = `/apolices/${policyId}/endosso/${tipo}/${subtypeId}`;
  return endorsementId ? `${base}?endosso=${endorsementId}` : base;
}

export const rotulosDosCampos: Record<EndorsementChangeField, string> = {
  legal_name: 'Razao social',
  social_name: 'Nome social',
  trade_name: 'Nome fantasia',
  address: 'Endereco',
  insured_amount: 'Valor IS modalidade principal',
  coverage_start: 'Inicio da vigencia',
  coverage_end: 'Final da vigencia',
  complementary_insured_amount: 'Valor IS modalidade complementar',
  object_text: 'Objeto da apolice',
  particular_clause: 'Condicao particular',
};

export const rotulosDosEscopos: Record<EndorsementChangeScope, string> = {
  tomador: 'Tomador',
  segurado: 'Segurado',
  apolice: 'Apolice',
};

export function enderecoVazio(): EndorsementAddress {
  return {
    zip_code: '',
    street: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
  };
}

/** O endereco da fotografia da parte, na forma da alteracao. */
export function enderecoDaFotografia(
  parte: PolicyParty | null | undefined,
): EndorsementAddress {
  if (!parte) return enderecoVazio();
  return {
    zip_code: parte.address.zip_code,
    street: parte.address.street,
    number: parte.address.number,
    complement: parte.address.complement,
    district: parte.address.district,
    city: parte.address.city,
    state: parte.address.state,
  };
}

export function enderecoEmTexto(endereco: EndorsementAddress | undefined): string {
  if (!endereco) return '';
  const rua = [endereco.street, endereco.number].filter(Boolean).join(', ');
  const resto = [endereco.complement, endereco.district].filter(Boolean).join(' - ');
  const cidade = [endereco.city, endereco.state].filter(Boolean).join('/');
  const cep = endereco.zip_code
    ? `CEP ${endereco.zip_code.replace(/^(\d{5})(\d{3})$/, '$1-$2')}`
    : '';
  return [rua, resto, cidade, cep].filter(Boolean).join(' - ');
}

/** Escreve um lado da alteracao para a tela. */
export function valorEmTexto(valor: EndorsementChangeValue | undefined): string {
  if (!valor) return '';
  if (valor.address) return enderecoEmTexto(valor.address);
  if (valor.amount) return formatMoney(valor.amount);
  if (valor.date) return formatCivilDate(valor.date);
  return valor.text ?? '';
}

/** O texto de um dado da fotografia da parte. */
export function textoDaParte(
  parte: PolicyParty | null | undefined,
  campo: 'legal_name' | 'social_name' | 'trade_name',
): string {
  if (!parte) return '';
  if (campo === 'legal_name') return parte.legal_name;
  if (campo === 'trade_name') return parte.trade_name;
  return parte.social_name ?? '';
}

async function somaDoArquivoDoEndosso(arquivo: File): Promise<string> {
  const conteudo = await arquivo.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', conteudo);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Envio do arquivo do documento do endosso em tres passos: reservar a versao, subir o
 * arquivo para o endereco assinado e confirmar. O envio e pelo endosso, e nao pelo
 * repositorio geral, que a corretora nao le.
 */
export async function enviarArquivoDoEndosso(
  endorsementId: string,
  documentId: string,
  arquivo: File,
): Promise<void> {
  const ticket = await endorsementWizard.startUpload(endorsementId, documentId, {
    filename: arquivo.name,
    content_type: arquivo.type || 'application/octet-stream',
    byte_size: arquivo.size,
    checksum: await somaDoArquivoDoEndosso(arquivo),
  });
  const resposta = await fetch(ticket.upload_url, { method: 'PUT', body: arquivo });
  if (!resposta.ok) throw new Error('Arquivo invalido, tente novamente!');
  await endorsementWizard.confirmUpload(endorsementId, documentId, ticket.version_id);
}
